import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { grantConsent, denyConsent } from '../lib/analytics';

// 2026-02 legal audit (Fix #14): mirror the user's cookie-banner
// choice into the unified `marketing_captures` audit table via the
// backend so we have a provable consent trail. Best-effort - failures
// must never block the UI from dismissing the banner.
const persistCookieChoice = (choice) => {
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) return;
    let sessionId = null;
    try {
      sessionId = window.localStorage.getItem('bp_session_id');
      if (!sessionId) {
        sessionId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem('bp_session_id', sessionId);
      }
    } catch { /* private mode / disabled storage */ }
    const body = {
      choice,
      session_id: sessionId,
      source_page: typeof window !== 'undefined' ? window.location.pathname : null,
    };
    // Fire-and-forget. keepalive lets the request survive a fast
    // navigation away from the page.
    fetch(`${apiUrl}/api/cookie-consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const location = useLocation();
  // Don't surface the cookie banner inside the workspace app shell - it has
  // its own auth-screen layouts where the banner overlaps brand surface.
  const insideWorkspace = location.pathname.startsWith('/workspace');

  useEffect(() => {
    // Check if user has already made a cookie choice. Guard storage reads:
    // storage-disabled in-app webviews (Instagram/Facebook) THROW on getItem,
    // which would otherwise crash the whole app to a blank screen.
    let cookieChoice = null;
    try {
      cookieChoice = localStorage.getItem('buildpilot_cookie_consent');
    } catch { /* storage unavailable - treat as no prior choice */ }
    if (cookieChoice) return undefined;

    // 2026-06-25: Auto-fade after the user has navigated through
    // 4 pages without making an explicit choice. We don't claim
    // implicit consent (the stored value stays absent, banner
    // returns on next session) - we just stop pestering once it's
    // clear they intend to keep using the site. Tracked per-tab via
    // sessionStorage so opening a new tab resets the counter.
    let views = 0;
    try {
      views = parseInt(window.sessionStorage.getItem('bp_cc_pageviews') || '0', 10) || 0;
      views += 1;
      window.sessionStorage.setItem('bp_cc_pageviews', String(views));
    } catch { /* private mode */ }
    if (views > 4) return undefined;

    // Small delay before showing the banner
    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Toggle `body[data-cookie-banner]` so any floating CTA (SmartFab,
  // QuickAddFab, BackToDashboardChip, etc.) can use the CSS hook
  // `body[data-cookie-banner="1"] .my-floater { bottom: 86px }` to
  // slide itself up above the banner. Without this, the banner and
  // the FABs collide at the bottom of the viewport on mobile.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const visible = showBanner && !insideWorkspace;
    if (visible) {
      document.body.setAttribute('data-cookie-banner', '1');
    } else {
      document.body.removeAttribute('data-cookie-banner');
    }
    return () => document.body.removeAttribute('data-cookie-banner');
  }, [showBanner, insideWorkspace]);

  // Let users change their mind at any time: a "Cookie preferences" control
  // (e.g. in the footer) dispatches this event or calls the global to
  // re-open the banner so they can switch Accept <-> Decline.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reopen = () => setShowBanner(true);
    window.addEventListener('bp:open-cookie-settings', reopen);
    window.bpOpenCookieSettings = reopen;
    return () => window.removeEventListener('bp:open-cookie-settings', reopen);
  }, []);

  const handleAccept = () => {
    try { localStorage.setItem('buildpilot_cookie_consent', 'accepted'); } catch { /* storage disabled */ }
    persistCookieChoice('accepted');
    grantConsent();            // Consent Mode v2: analytics + ads granted
    setShowBanner(false);
  };

  const handleDecline = () => {
    try { localStorage.setItem('buildpilot_cookie_consent', 'declined'); } catch { /* storage disabled */ }
    persistCookieChoice('declined');
    denyConsent();             // Consent Mode v2: keep analytics + ads denied
    setShowBanner(false);
  };

  const handleClose = () => {
    setShowBanner(false);
  };

  if (!showBanner) return null;
  if (insideWorkspace) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] w-[calc(100%-1.5rem)] sm:w-auto"
    >
      <div className="bg-[#1d1d1f]/85 bp-glass-surface bp-glass-edge--dark rounded-full shadow-lg shadow-black/20 border border-white/10 pl-4 sm:pl-5 pr-9 sm:pr-12 py-2 sm:py-2.5 relative">
        <button
          onClick={handleClose}
          className="absolute top-1/2 -translate-y-1/2 right-2.5 sm:right-3 text-white/60 hover:text-white transition-colors p-1"
          aria-label="Close"
          data-testid="cookie-consent-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-3 gap-y-1.5 sm:gap-y-2 text-center">
          <p className="text-[11.5px] sm:text-[12px] text-white leading-[1.4] m-0">
            <span className="hidden sm:inline">We use cookies for analytics and advertising to improve your experience. You can accept or decline. </span>
            <span className="sm:hidden">Analytics &amp; advertising cookies. </span>
            <a href="/cookies" className="text-[#7fb3e8] hover:underline">
              Learn more
            </a>
          </p>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              onClick={handleDecline}
              className="px-2.5 sm:px-3 py-1 text-[11.5px] sm:text-[12px] font-medium text-white/70 hover:text-white transition-colors rounded-full border border-white/20 hover:border-white/40"
              data-testid="cookie-consent-decline"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              className="px-2.5 sm:px-3 py-1 text-[11.5px] sm:text-[12px] font-medium text-white gradient-blue-bg rounded-full hover:opacity-90 transition-all"
              data-testid="cookie-consent-accept"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
